/**
 * DemoProjectCard.jsx - Card per progetti demo
 * Mostra badge "Demo", tooltip educativo, e CTA (Edit, Duplicate, Create New)
 * Se utente modifica → is_demo = false
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Play, Edit3, Copy, Plus, ArrowRight, Sparkles, 
  Info, FolderKanban, MoreVertical, Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';

/**
 * Demo Badge Component
 */
const DemoBadge = ({ showTooltip = true }) => {
  const badge = (
    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#b08b5c]/20 border border-[#b08b5c]/30 text-[#b08b5c] text-xs font-medium">
      <Sparkles className="w-3 h-3" />
      <span>Demo</span>
    </div>
  );

  if (!showTooltip) return badge;

  return (
    <TooltipProvider>
      <Tooltip delayDuration={200}>
        <TooltipTrigger asChild>
          <div className="cursor-help">
            {badge}
          </div>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-xs bg-[#1a1a1f] border-white/10 text-white p-3"
        >
          <div className="space-y-2">
            <p className="font-medium text-sm">Progetto Demo</p>
            <p className="text-xs text-white/60 leading-relaxed">
              Questo è un progetto dimostrativo creato automaticamente per aiutarti a scoprire le funzionalità. 
              Puoi modificarlo, duplicarlo o creare un nuovo progetto da zero.
            </p>
            <div className="flex items-center gap-1.5 text-xs text-[#b08b5c] pt-1">
              <Info className="w-3 h-3" />
              <span>Quando modifichi, diventa un progetto reale</span>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Main Demo Project Card Component
 */
const DemoProjectCard = ({ 
  project, 
  onEdit, 
  onDuplicate, 
  onDelete,
  onCreateNew,
  testId 
}) => {
  const navigate = useNavigate();
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateName, setDuplicateName] = useState(`${project.name} (Copia)`);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Get cover image
  const coverImage = project.cover_image || 
    project.project_materials?.[0]?.material_image ||
    'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&q=80';

  // Material count
  const materialCount = project.project_materials?.length || 0;

  // Handle duplicate with custom name
  const handleDuplicate = async () => {
    if (!duplicateName.trim()) {
      toast.error('Inserisci un nome per il progetto');
      return;
    }
    
    setIsDuplicating(true);
    try {
      await onDuplicate(project.id, duplicateName.trim());
      setShowDuplicateDialog(false);
      toast.success('Progetto duplicato con successo!');
    } catch (err) {
      toast.error('Errore nella duplicazione');
    } finally {
      setIsDuplicating(false);
    }
  };

  // Handle edit (goes to project detail)
  const handleEdit = () => {
    if (onEdit) {
      onEdit(project);
    } else {
      navigate(`/project/${project.id}`);
    }
  };

  return (
    <>
      <div 
        className="group relative"
        data-testid={testId || `demo-project-card-${project.id}`}
      >
        {/* Card Container */}
        <div className="luxury-card overflow-hidden hover:luxury-glow-gold transition-all duration-500">
          {/* Cover Image */}
          <Link to={`/project/${project.id}`} className="block">
            <div className="relative aspect-[4/3] overflow-hidden">
              <img 
                src={coverImage}
                alt={project.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              
              {/* Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/20 to-transparent" />
              
              {/* Demo Badge - Top Left */}
              {project.is_demo && (
                <div className="absolute top-3 left-3 z-10">
                  <DemoBadge />
                </div>
              )}
              
              {/* Material Count - Top Right */}
              <div className="absolute top-3 right-3 z-10">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/40 backdrop-blur-sm text-white/80 text-xs">
                  <FolderKanban className="w-3 h-3" />
                  <span>{materialCount} materiali</span>
                </div>
              </div>
              
              {/* Hover View Button */}
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm">
                  <Play className="w-4 h-4" fill="currentColor" />
                  Apri Progetto
                </div>
              </div>
            </div>
          </Link>
          
          {/* Content */}
          <div className="p-4">
            {/* Header */}
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-medium text-white truncate">{project.name}</h3>
                {project.client && (
                  <p className="text-xs text-white/50 truncate">{project.client}</p>
                )}
              </div>
              
              {/* Actions Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-white/40 hover:text-white hover:bg-white/10"
                    data-testid={`project-menu-${project.id}`}
                  >
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1a1a1f] border-white/10">
                  <DropdownMenuItem 
                    onClick={handleEdit}
                    className="text-white/80 hover:text-white hover:bg-white/10 cursor-pointer"
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Modifica
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => setShowDuplicateDialog(true)}
                    className="text-white/80 hover:text-white hover:bg-white/10 cursor-pointer"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Duplica
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-white/10" />
                  <DropdownMenuItem 
                    onClick={() => onDelete && onDelete(project.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Elimina
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {/* Description (truncated) */}
            {project.description && (
              <p className="text-xs text-white/40 line-clamp-2 mb-3">
                {project.description}
              </p>
            )}
            
            {/* CTA Buttons - Only for Demo Projects */}
            {project.is_demo && (
              <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEdit}
                  className="flex-1 h-8 text-xs text-white/70 hover:text-white hover:bg-white/10"
                  data-testid={`demo-edit-${project.id}`}
                >
                  <Edit3 className="w-3 h-3 mr-1.5" />
                  Modifica
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDuplicateDialog(true)}
                  className="flex-1 h-8 text-xs text-white/70 hover:text-white hover:bg-white/10"
                  data-testid={`demo-duplicate-${project.id}`}
                >
                  <Copy className="w-3 h-3 mr-1.5" />
                  Duplica
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onCreateNew}
                  className="flex-1 h-8 text-xs text-[#b08b5c] hover:text-[#c9a577] hover:bg-[#b08b5c]/10"
                  data-testid={`demo-create-new-${project.id}`}
                >
                  <Plus className="w-3 h-3 mr-1.5" />
                  Nuovo
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Duplicate Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="bg-[#0a0a0f] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-lg font-light">Duplica Progetto</DialogTitle>
            <DialogDescription className="text-white/50 text-sm">
              Crea una copia di "{project.name}" come nuovo progetto reale.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="duplicate-name" className="text-white/70 text-sm">
                Nome del nuovo progetto
              </Label>
              <Input
                id="duplicate-name"
                value={duplicateName}
                onChange={(e) => setDuplicateName(e.target.value)}
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                placeholder="Inserisci un nome..."
                data-testid="duplicate-name-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setShowDuplicateDialog(false)}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              Annulla
            </Button>
            <Button
              onClick={handleDuplicate}
              disabled={isDuplicating || !duplicateName.trim()}
              className="bg-[#b08b5c] hover:bg-[#c9a577] text-white"
              data-testid="confirm-duplicate-btn"
            >
              {isDuplicating ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  Duplicazione...
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4 mr-2" />
                  Duplica
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Demo Welcome Banner - Shown when user only has demo project
 */
export const DemoWelcomeBanner = ({ onDismiss }) => {
  return (
    <div className="luxury-card p-6 mb-6 border-[#b08b5c]/20 bg-gradient-to-r from-[#b08b5c]/5 to-transparent">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#b08b5c]/10 border border-[#b08b5c]/20 flex items-center justify-center flex-shrink-0">
          <Sparkles className="w-6 h-6 text-[#b08b5c]" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-light text-white mb-1">Benvenuto in SlabReality!</h3>
          <p className="text-sm text-white/50 mb-3 leading-relaxed">
            Abbiamo creato un progetto demo per aiutarti a scoprire le funzionalità. 
            Puoi esplorarlo, modificarlo o creare il tuo primo progetto personalizzato.
          </p>
          <div className="flex items-center gap-3">
            <Link 
              to="/materials"
              className="inline-flex items-center gap-2 text-sm text-[#b08b5c] hover:text-[#c9a577] transition-colors"
            >
              Esplora i materiali
              <ArrowRight className="w-4 h-4" />
            </Link>
            {onDismiss && (
              <button 
                onClick={onDismiss}
                className="text-xs text-white/30 hover:text-white/50 transition-colors"
              >
                Nascondi
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export { DemoBadge };
export default DemoProjectCard;
